import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery, getApiBaseUrl } from 'modules/api/utils';
import { mapTagFiltersToString } from 'store/utils';
import type { TagDemographic } from 'tags';

import {
  ConversationInsightsData,
  ExecutiveSummaryData,
  KeyMetricsData,
  SessionExplorationData,
} from '../eventsReport';
import {
  mapAnswerStatisticsList,
  mapEventsReportDataToInsightsReportData,
  mapExecutiveSummaryData,
} from '../eventsReport/mappers';
import { AnswerStatistics, InsightsReportData, transformDemographicsResponse } from '../insightsReport';
import {
  type FilterUsersApiParams,
  type FilterUsersPayload,
  PublicEventsReportAnswersApiParams,
  PublicEventsReportApiParams,
  PublicEventsReportDemographicsApiParams,
} from './types';

const apiV2 = getApiBaseUrl('v2');
const apiV3 = getApiBaseUrl('v3');

export const publicEventsReportApi = createApi({
  reducerPath: 'publicEventsReportApi',
  tagTypes: ['REPORTS', 'REPORT_DEMOGRAPHICS', 'REPORT_ANSWERS'],
  baseQuery: axiosBaseQuery({ baseURL: '/' }),
  endpoints: (builder) => ({
    getPublicEventsReportById: builder.query<InsightsReportData, PublicEventsReportApiParams>({
      providesTags: (result) => (result?.id ? [{ type: 'REPORTS', id: result.id }] : []),
      query: ({ reportId, pin }) => ({
        url: `${apiV2}/event/report/${reportId}/public`,
        headers: { pin },
      }),
      transformResponse: mapEventsReportDataToInsightsReportData,
    }),
    getPublicEventsReportKeyMetrics: builder.query<KeyMetricsData, PublicEventsReportApiParams>({
      providesTags: (_result, _error, args) => (args.reportId ? [{ type: 'REPORTS', id: args.reportId }] : []),
      query: ({ reportId, pin }) => ({
        url: `${apiV2}/event/report/${reportId}/public/event-metrics`,
        headers: { pin },
      }),
    }),
    getPublicEventsReportDemographics: builder.query<TagDemographic[], PublicEventsReportDemographicsApiParams>({
      providesTags: (result, _error, { reportId }) =>
        result?.length !== undefined ? [{ type: 'REPORT_DEMOGRAPHICS', id: reportId }] : [],
      query: ({ reportId, pin, tagFilters }) => ({
        url: `${apiV2}/event/report/${reportId}/public/event-demographics`,
        params: {
          tagFilters: mapTagFiltersToString(tagFilters),
        },
        headers: { pin },
      }),
      transformResponse: transformDemographicsResponse,
    }),
    getPublicEventsReportAnswers: builder.query<AnswerStatistics[], PublicEventsReportAnswersApiParams>({
      providesTags: (result, _error, { reportId }) =>
        result?.length !== undefined ? [{ type: 'REPORT_ANSWERS', id: reportId }] : [],
      serializeQueryArgs: ({ queryArgs, ...args }) => ({
        ...args,
        queryArgs: {
          ...queryArgs,
          tagFilters: mapTagFiltersToString(queryArgs.tagFilters),
        },
      }),
      query: ({ reportId, tagFilters, assignmentType, pin }) => ({
        url: `${apiV2}/event/report/${reportId}/public/answers`,
        params: {
          eventQuestionAssignmentType: assignmentType,
          tagFilters: mapTagFiltersToString(tagFilters),
        },
        headers: { pin },
      }),
      transformResponse: mapAnswerStatisticsList,
    }),
    /**
     * V2
     */
    getPublicSessionExplorationData: builder.query<SessionExplorationData, PublicEventsReportApiParams>({
      providesTags: (result, _error, { reportId }) =>
        result?.topics.length !== undefined ? [{ type: 'REPORT_ANSWERS', id: reportId }] : [],
      query: ({ reportId, pin }) => ({
        url: `${apiV3}/report/${reportId}/sessionExplorationData/public`,
        headers: { pin },
      }),
    }),
    getPublicConversationInsightsData: builder.query<ConversationInsightsData, PublicEventsReportApiParams>({
      providesTags: (result, _error, { reportId }) =>
        result?.topics.length !== undefined ? [{ type: 'REPORT_ANSWERS', id: reportId }] : [],
      query: ({ reportId, pin }) => ({
        url: `${apiV3}/report/${reportId}/conversationInsights/public`,
        headers: { pin },
      }),
    }),
    getPublicExecutiveSummary: builder.query<ExecutiveSummaryData, PublicEventsReportApiParams>({
      providesTags: (result, _error, { reportId }) =>
        result?.isPublished !== undefined ? [{ type: 'REPORTS', id: reportId }] : [],
      query: ({ reportId, pin }) => ({
        url: `${apiV3}/report/${reportId}/executiveSummary/public`,
        headers: { pin },
      }),
      transformResponse: mapExecutiveSummaryData,
    }),
    filterUsers: builder.mutation<FilterUsersPayload, FilterUsersApiParams>({
      query: ({ reportId, filters, pin }) => ({
        url: `${apiV3}/report/${reportId}/filterUsersByName/public`,
        method: 'POST',
        data: { filters },
        headers: { pin },
      }),
    }),
  }),
});

export const {
  useGetPublicEventsReportByIdQuery,
  useGetPublicEventsReportDemographicsQuery,
  useGetPublicEventsReportKeyMetricsQuery,
  useGetPublicEventsReportAnswersQuery,
  useGetPublicSessionExplorationDataQuery,
  useGetPublicConversationInsightsDataQuery,
  useGetPublicExecutiveSummaryQuery,
  useFilterUsersMutation: usePublicFilterUsersMutation,
} = publicEventsReportApi;
